import { useCallback } from 'react'

import { Box, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { Tag } from '@workwave-tidal/core/components'
import { Call, ContentCopy, CreditCard, Textsms } from '@workwave-tidal/core/icons'

import { useNotification } from '@/hooks'
import { copyToClipboard } from '@/utils'
import { useFetchPhoneNumbers } from '../../../hooks/useFetchPhoneNumbers'
import { useTexts } from '../useTexts'

import { PlanDetailsWarningCard } from './PlanDetailsWarningCard'

import {
  MainProgressBarStyle,
  OutlinedPaperStyle,
  PaperStyle,
  SecondaryTagStyle,
  SubProgressBarStyle,
} from '../../commonStyles'

interface Props {
  callCredits: number
  smsCount: number
  smsLimit: number
  trial: boolean
  allowSmsOverage: boolean
}

export function CreditBalance(props: Props) {
  const { smsCount, callCredits, smsLimit, trial, allowSmsOverage } = props
  const texts = useTexts()
  const toast = useNotification()

  const phoneNumbers = useFetchPhoneNumbers()

  const smsPercentage = Math.round((smsCount * 100) / smsLimit)
  const callsPercentage = Math.round((callCredits * 100) / smsLimit)
  const usagePercentage = smsPercentage + callsPercentage
  const totalCredits = smsCount + callCredits

  let progressBarColor: 'primary' | 'warning' | 'error' = 'primary'

  if (usagePercentage >= 100) progressBarColor = 'error'
  else if (usagePercentage >= 75) progressBarColor = 'warning'

  const copyPhoneNumbers = useCallback(async () => {
    if (!phoneNumbers) return

    const copied = await copyToClipboard(phoneNumbers.join(' '))
    copied
      ? toast.success(texts.copyPhoneNumbersSuccess, { autoClose: 5000 })
      : toast.error(texts.copyPhoneNumbersError, { autoClose: 5000 })
  }, [toast, texts, phoneNumbers])

  return (
    <Paper elevation={0} sx={OutlinedPaperStyle} variant="outlined">
      <Stack direction="column" spacing={2}>
        <Stack direction="column" spacing={0.5}>
          <Typography variant="subtitle3">
            <Box display="flex" alignItems="center">
              <CreditCard sx={{ mr: 1 }} />
              {texts.creditBalanceLabel}
            </Box>
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {texts.creditBalanceDescription}
          </Typography>
        </Stack>

        <Stack direction="column" spacing={1}>
          <Stack direction="row" display="flex" alignItems="center" spacing={2}>
            <Box width="100%">
              <LinearProgress
                variant="determinate"
                value={usagePercentage}
                color={progressBarColor}
                sx={MainProgressBarStyle}
              />
            </Box>
            <Typography variant="body2">{`${usagePercentage}%`}</Typography>
          </Stack>
          <Typography variant="caption">{texts.creditConsumption(totalCredits)}</Typography>
        </Stack>

        <PlanDetailsWarningCard allowSmsOverage={allowSmsOverage} trial={trial} />

        {/* usage overview */}
        <Stack direction="column" spacing={1}>
          <Typography variant="subtitle1" color="text.secondary">
            {texts.usageOverviewLabel}
          </Typography>
          <Stack direction="column" spacing={2}>
            {/* calls */}
            <Paper elevation={0} sx={OutlinedPaperStyle} variant="outlined">
              <Stack direction="column" spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">
                    <Box display="flex" alignItems="center">
                      <Call sx={{ mr: 1 }} />
                      {texts.callsLabel}
                    </Box>
                  </Typography>

                  <Tag
                    sx={SecondaryTagStyle}
                    label={texts.creditConsumption(callCredits)}
                    color="primary"
                  />
                </Stack>

                <Stack direction="row" display="flex" alignItems="center" spacing={2}>
                  <Box width="100%">
                    <LinearProgress
                      variant="determinate"
                      value={callsPercentage}
                      color={progressBarColor}
                      sx={SubProgressBarStyle}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >{`${callsPercentage}%`}</Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* sms */}
            <Paper elevation={0} sx={OutlinedPaperStyle} variant="outlined">
              <Stack direction="column" spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">
                    <Box display="flex" alignItems="center">
                      <Textsms sx={{ mr: 1 }} />
                      {texts.smsLabel}
                    </Box>
                  </Typography>

                  <Tag
                    sx={SecondaryTagStyle}
                    label={texts.creditConsumption(smsCount)}
                    color="primary"
                  />
                </Stack>

                <Stack direction="row" display="flex" alignItems="center" spacing={2}>
                  <Box width="100%">
                    <LinearProgress
                      variant="determinate"
                      value={smsPercentage}
                      color={progressBarColor}
                      sx={SubProgressBarStyle}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >{`${smsPercentage}%`}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="column">
                    <Typography variant="subtitle2" color="text.secondary">
                      {texts.phoneNumbersTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {texts.phoneNumbersSubtitle}
                    </Typography>
                  </Stack>
                  <Tooltip title={texts.phoneNumbersCopyButtonLabel} placement="bottom">
                    <IconButton onClick={copyPhoneNumbers} size="small">
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {/* phone numbers */}
                <Paper elevation={0} sx={PaperStyle}>
                  <Stack direction="column" spacing={1}>
                    {phoneNumbers?.map(phoneNumber => (
                      <Typography variant="body2" key={phoneNumber}>
                        {phoneNumber}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
}
